/* eslint react-hooks/exhaustive-deps: "warn" */

import { type ReactNode, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './RegisIntegration.module.scss';

import { LANG_ORDER, type LangKey, SNIPPETS } from './RegisIntegration.data';

type Translate = (key: string) => string;

type Row = {
  term: string;
  sub: string;
  value: ReactNode;
};

const getRows = (t: Translate): Row[] => [
  {
    term: 'REST',
    sub: 'POST /v1/regis/query',
    value: (
      <>
        {t('A single endpoint takes')} <code>prompt</code>{' '}
        {t(
          'and returns the answer with its sources. The rest of the v2 API stays available for structured calls against the same records.',
        )}
      </>
    ),
  },
  {
    term: 'MCP server',
    sub: 'mcp.globaldatabase.com',
    value: t(
      'Expose the same dataset as tools inside an assistant or an internal agent. The agent asks in the same plain language and gets the same sourced answer back.',
    ),
  },
  {
    term: t('Inside your product'),
    sub: t('redistribution licence'),
    value: t(
      "Regis can sit behind your own interface and answer your customers' questions under your brand. Redistribution terms are set in the licence rather than assumed.",
    ),
  },
];

export const RegisIntegration = () => {
  const { t } = useInt();
  const rootRef = useRef<HTMLDivElement>(null);
  const visible = useIntersection(rootRef, { threshold: 0.1 });

  const rows = getRows(t);

  const [lang, setLang] = useState<LangKey>('curl');
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard
      .writeText(SNIPPETS[lang].raw)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1600);
      })
      .catch(() => undefined);
  };

  return (
    <Section className={styles.root}>
      <div ref={rootRef} className={cn(styles.content, visible && styles.contentIn)}>
        <div className={styles.head}>
          <TitleBlock
            align="left"
            title={t('One POST, a token header, a string.')}
            description={t(
              'Authentication is a token in the header. There is no SDK to install and no query language to learn. If your service can make an HTTP request, it can call Regis.',
            )}
          />
        </div>

        <div className={styles.code}>
          <div className={styles.codeHead}>
            <span className={styles.dots}>
              <span />
              <span />
              <span />
            </span>

            <div className={styles.codeTabs} role="tablist" aria-label={t('Code language')}>
              {LANG_ORDER.map((key) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={lang === key}
                  className={cn(styles.codeTab, lang === key && styles.codeTabActive)}
                  onClick={() => setLang(key)}
                >
                  {SNIPPETS[key].label}
                </button>
              ))}
            </div>

            <span className={styles.spacer} />

            <button type="button" className={cn(styles.copyBtn, copied && styles.copyBtnDone)} onClick={handleCopy}>
              {copied ? (
                <>
                  <span className={styles.copyIcon}>&#10003;</span>
                  {t('Copied')}
                </>
              ) : (
                t('Copy')
              )}
            </button>
          </div>

          <pre className={styles.pre} dangerouslySetInnerHTML={{ __html: SNIPPETS[lang].html }} />
        </div>

        <div className={styles.rows}>
          {rows.map((row) => (
            <div key={row.term} className={styles.row}>
              <div className={styles.rowKey}>
                {row.term}
                <span className={styles.rowSub}>{row.sub}</span>
              </div>
              <div className={styles.rowValue}>{row.value}</div>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
};
